import {
  CreateWorkLocationInput,
  OrganizationDetailFragment,
  useCreateWorkLocationMutation,
  useResponseNotification,
  useUpdateWorkLocationMutation,
  type CreateWorkLocationMutationVariables,
} from '@alice-financial/alice-sdk'
import {
  composeMutationCallbacks,
  UseGqlMutationCallbacks,
  useMutationOptionsWithInvalidation,
} from '@alice-financial/api'
import { QueryKey, useMutation, type UseMutationResult } from '@tanstack/react-query'
import { WorkLocationFormInputs } from './types'
import { useUpdateWorkLocationLogo } from './useUpdateWorkLocationLogo'

export type LocationData = Omit<CreateWorkLocationInput, 'orgId'>

type UseManageWorkLocationInput = WorkLocationFormInputs
type UseCreateLocationMutationOptions = UseGqlMutationCallbacks<
  { id: number | string },
  UseManageWorkLocationInput
>

const locationToInput = (orgId: number, locationData: LocationData): CreateWorkLocationMutationVariables => {
  return { input: { ...locationData, orgId } }
}

// This hook is meant to take a form state and determine, based on the presence of an id field,
// whether to execute an update or a create mutation. In either case, it will also fire a second
// updateWorkLocationLogo mutation after success of the original write - this is because logo
// upload actually exists on a separate endpoint.
export const useManageWorkLocation = (
  org: OrganizationDetailFragment,
  invalidationKeys: QueryKey[],
  mutationOptions?: UseCreateLocationMutationOptions
): UseMutationResult<{ id: string | number | undefined }, unknown, WorkLocationFormInputs, unknown> => {
  const { notifyError } = useResponseNotification()
  const composedMutationOptions = composeMutationCallbacks(mutationOptions, {
    onSuccess: (data) => {
      if (!data.id) {
        notifyError('Could not create/update location')
      }
    },
    onError: (error) => {
      notifyError(error.message)
    },
  })

  const mutationOptionsWithInvalidation = useMutationOptionsWithInvalidation(
    composedMutationOptions,
    invalidationKeys
  )

  const { mutateAsync: createWorkLocation } = useCreateWorkLocationMutation()
  const { mutateAsync: updateWorkLocation } = useUpdateWorkLocationMutation()
  const { mutateAsync: updateLocationLogo } = useUpdateWorkLocationLogo(org)

  return useMutation(async ({ name, address, latitude, longitude, logo, id }: WorkLocationFormInputs) => {
    const locationData: LocationData = {
      name,
      address,
      latitude,
      longitude,
    }
    const locationInputData = locationToInput(org.id, locationData)
    const mutationPromise = id
      ? updateWorkLocation({ input: { id, ...locationData } }).then(() => id)
      : createWorkLocation(locationInputData).then((result) => result.createWorkLocation?.workLocation?.id)

    const workLocationId = await mutationPromise

    if (!workLocationId) throw new Error('Work location not created')
    if (logo !== undefined) updateLocationLogo({ logo, id: parseInt(workLocationId.toString(), 10) })

    return { id: workLocationId }
  }, mutationOptionsWithInvalidation)
}
